import React, { useState } from 'react';
import { BuildingIcon, SearchIcon, CheckCircleIcon, XCircleIcon, RefreshCwIcon } from 'lucide-react';
export function CompanyPersonaSetup({
  data,
  updateData
}) {
  const [companyName, setCompanyName] = useState(data.companyName || '');
  const [companyUrl, setCompanyUrl] = useState(data.companyUrl || '');
  const [isDetecting, setIsDetecting] = useState(false);
  const [isEnriching, setIsEnriching] = useState(false);
  const [isValidUrl, setIsValidUrl] = useState(true);
  const [enrichmentComplete, setEnrichmentComplete] = useState(false);
  const handleCompanyNameChange = e => {
    setCompanyName(e.target.value);
    updateData('companyName', e.target.value);
  };
  const handleCompanyUrlChange = e => {
    const url = e.target.value;
    setCompanyUrl(url);
    setIsValidUrl(validateUrl(url));
    updateData('companyUrl', url);
  };
  const validateUrl = url => {
    if (!url) return true;
    const pattern = /^(https?:\/\/)?(www\.)?[a-zA-Z0-9-]+(\.[a-zA-Z0-9-]+)+([/?#].*)?$/;
    return pattern.test(url);
  };
  const detectFromEmail = () => {
    setIsDetecting(true);
    // Simulate detection from email
    setTimeout(() => {
      const detectedDomain = 'nexustech.com';
      const detectedCompany = 'Nexus Technologies';
      setCompanyUrl(detectedDomain);
      setCompanyName(detectedCompany);
      updateData('companyUrl', detectedDomain);
      updateData('companyName', detectedCompany);
      setIsDetecting(false);
      enrichCompanyData(detectedCompany, detectedDomain);
    }, 1500);
  };
  const enrichCompanyData = (name, url) => {
    if (!name || !url) return;
    setIsEnriching(true);
    // Simulate API enrichment
    setTimeout(() => {
      const enrichedData = {
        name: name,
        url: url,
        industry: 'Enterprise Software',
        size: '250-500 employees',
        founded: 2014,
        location: 'San Francisco, CA',
        description: 'Nexus Technologies is a growing player in the data integration space, competing with larger platforms like MuleSoft and Boomi while targeting mid-market enterprises.',
        funding: 'Series C ($45M)',
        keyPeople: [{
          name: 'Michael Clutterbuck',
          role: 'CTO'
        }, {
          name: 'Teena Reale',
          role: 'VP of Product'
        }],
        competitors: ['MuleSoft', 'Boomi', 'Zapier Enterprise'],
        techStack: ['AWS', 'React', 'Node.js', 'PostgreSQL', 'Salesforce']
      };
      updateData('companyPersona', enrichedData);
      setIsEnriching(false);
      setEnrichmentComplete(true);
    }, 2000);
  };
  return <div data-id="element-526">
      <h2 className="text-2xl font-bold mb-2" data-id="element-527">Company Profile Setup</h2>
      <p className="text-[#F5F7FA] text-opacity-70 mb-6" data-id="element-528">
        Let's start by understanding your company to provide more relevant
        meeting insights.
      </p>
      <div className="space-y-6" data-id="element-529">
        <div data-id="element-530">
          <label htmlFor="companyName" className="block text-sm font-medium mb-2" data-id="element-531">
            Company Name
          </label>
          <input type="text" id="companyName" value={companyName} onChange={handleCompanyNameChange} placeholder="Enter your company name" className="w-full bg-[#1E2A38] border border-[#F5F7FA] border-opacity-10 rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-[#C8F169] focus:ring-opacity-50" data-id="element-532" />
        </div>
        <div data-id="element-533">
          <label htmlFor="companyUrl" className="block text-sm font-medium mb-2" data-id="element-534">
            Company Website
          </label>
          <div className="relative" data-id="element-535">
            <input type="text" id="companyUrl" value={companyUrl} onChange={handleCompanyUrlChange} placeholder="example.com" className={`w-full bg-[#1E2A38] border ${!isValidUrl ? 'border-red-500' : 'border-[#F5F7FA] border-opacity-10'} rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-[#C8F169] focus:ring-opacity-50`} data-id="element-536" />
            {!isValidUrl && <p className="mt-1 text-xs text-red-500" data-id="element-537">
                Please enter a valid URL
              </p>}
          </div>
        </div>
        <div className="flex items-center justify-between" data-id="element-538">
          <button onClick={detectFromEmail} disabled={isDetecting} className="flex items-center text-[#C8F169] hover:text-opacity-80 transition-colors" data-id="element-539">
            {isDetecting ? <>
                <RefreshCwIcon className="h-4 w-4 mr-2 animate-spin" data-id="element-540" />
                Detecting...
              </> : <>
                <SearchIcon className="h-4 w-4 mr-2" data-id="element-541" />
                Auto-detect from email domain
              </>}
          </button>
          <button onClick={() => enrichCompanyData(companyName, companyUrl)} disabled={!companyName || !companyUrl || isEnriching || !isValidUrl} className={`flex items-center px-4 py-1.5 rounded-lg transition-colors ${!companyName || !companyUrl || isEnriching || !isValidUrl ? 'bg-[#F5F7FA] bg-opacity-5 text-[#F5F7FA] text-opacity-30 cursor-not-allowed' : 'bg-[#F5F7FA] bg-opacity-10 text-[#F5F7FA] hover:bg-opacity-20'}`} data-id="element-542">
            {isEnriching ? <>
                <RefreshCwIcon className="h-4 w-4 mr-2 animate-spin" data-id="element-543" />
                Enriching...
              </> : 'Enrich Data'}
          </button>
        </div>
        {enrichmentComplete && data.companyPersona && <div className="mt-6 bg-[#1E2A38] border border-[#F5F7FA] border-opacity-10 rounded-lg p-4" data-id="element-544">
            <div className="flex items-center mb-4" data-id="element-545">
              <CheckCircleIcon className="h-5 w-5 text-green-500 mr-2" data-id="element-546" />
              <h3 className="font-medium" data-id="element-547">Company Profile Enriched</h3>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-548">
              <div data-id="element-549">
                <p className="text-sm text-[#F5F7FA] text-opacity-70 mb-1" data-id="element-550">
                  Industry
                </p>
                <p className="text-sm" data-id="element-551">{data.companyPersona.industry}</p>
              </div>
              <div data-id="element-552">
                <p className="text-sm text-[#F5F7FA] text-opacity-70 mb-1" data-id="element-553">
                  Size
                </p>
                <p className="text-sm" data-id="element-554">{data.companyPersona.size}</p>
              </div>
              <div data-id="element-555">
                <p className="text-sm text-[#F5F7FA] text-opacity-70 mb-1" data-id="element-556">
                  Founded
                </p>
                <p className="text-sm" data-id="element-557">{data.companyPersona.founded}</p>
              </div>
              <div data-id="element-558">
                <p className="text-sm text-[#F5F7FA] text-opacity-70 mb-1" data-id="element-559">
                  Funding
                </p>
                <p className="text-sm" data-id="element-560">{data.companyPersona.funding}</p>
              </div>
            </div>
            <div className="mt-4" data-id="element-561">
              <p className="text-sm text-[#F5F7FA] text-opacity-70 mb-1" data-id="element-562">
                Description
              </p>
              <p className="text-sm" data-id="element-563">{data.companyPersona.description}</p>
            </div>
            <div className="mt-4" data-id="element-564">
              <p className="text-sm text-[#F5F7FA] text-opacity-70 mb-1" data-id="element-565">
                Tech Stack
              </p>
              <div className="flex flex-wrap gap-2 mt-1" data-id="element-566">
                {data.companyPersona.techStack.map((tech, index) => <span key={index} className="text-xs bg-[#F5F7FA] bg-opacity-10 px-2 py-1 rounded" data-id="element-567">
                    {tech}
                  </span>)}
              </div>
            </div>
          </div>}
      </div>
    </div>;
}